import { Variant } from 'framer-motion';
import {
  GeneralShopListItem,
  Order,
  OrderBenefit,
  OrderProduct,
  OrderSubscription,
  ShopCart,
  ShopCartItem,
  ShopItem,
} from '../models';
import { groupByKey } from './groupByKey';

interface ShopItemInterface {
  variant: string | null;
  photo: string | null;
  benefit: string | null;
  plan: string | null;
  price: number;
  title: string;
  shopList: GeneralShopListItem[];
  compareAtPrice?: number;
}

export function generateShopItems(
  shopCart: ShopCart | Order,
  type: 'product' | 'benefit' | 'plan'
) {
  let items: ShopItem[] = [];

  // cada producto genera un documento por persona, si una persona lleva 5t veces un producto tinene 5 documentos
  // por lo tatno se agrupa los que son un mismo producto por una determianda key
  if (type === 'product') {
    items = groupByKey(shopCart.products, 'sku');
  } else if (type === 'benefit') {
    items = groupByKey(shopCart.benefits, 'title');
  } else if (type === 'plan') {
    items = groupByKey(shopCart.subscriptions, 'title');
  }
  // para cada producto se requiere armar  el shopItem

  if (type === 'plan' || type === 'product' || type === 'benefit') {
    const shopItems = Object.keys(items).map((key) => {
      const item = items[key][0];
      const clientsOrdersItems = items[key];
      return buildShopItem(item, clientsOrdersItems, type);
    });

    return shopItems;
  }
}

export function buildShopItem(
  item: OrderProduct | OrderBenefit | OrderSubscription,
  clientsOrdersItems: OrderProduct[] | OrderBenefit[] | OrderSubscription[],
  type: 'product' | 'benefit' | 'plan'
) {
  let shopItem: ShopItemInterface = {
    // info del producto
    variant: type === 'product' ? (item as OrderProduct).variant._id : null,
    photo:
      type === 'product'
        ? (item as OrderProduct)?.variant?.photos[0]?.src
        : null,
    benefit: type === 'benefit' ? (item as OrderBenefit)?.benefit?._id : null,
    plan: type === 'plan' ? (item as OrderSubscription)?.plan?._id : null,
    price: type !== 'benefit' ? item.price : (item as OrderBenefit)?.value,
    title: item.title,
    shopList: generateShopList(
      clientsOrdersItems as GeneralShopListItem[],
      type
    ),
  };

  if ((item as OrderProduct).variant) {
    const { compareAtPrice } = (item as OrderProduct).variant;
    shopItem = { ...shopItem, compareAtPrice };
  }

  return shopItem;
}

export function generateShopList<T extends GeneralShopListItem>(
  clientsOrdersItems: T[],
  type: 'product' | 'benefit' | 'plan'
) {
  const shopList = clientsOrdersItems.reduce((acc, item) => {
    const { client } = item;
    const benefitValue = type === 'benefit' ? item.price : null;
    const quantity = item.quantity ?? 1;
    // ver si esa persona ya esta en la lista para sumarle la cantidad o en defecto pushearlo al array

    const clientItemInShopList =
      type !== 'benefit'
        ? acc.find((_item) => _item.client === client)
        : acc.find(
            (_item) =>
              _item.client === client && _item.benefitPrice === benefitValue
          );

    if (clientItemInShopList) {
      clientItemInShopList.quantity += quantity;
    } else {
      acc.push({
        client,
        benefitValue,
        quantity,
      });
    }
    return acc;
  }, []);

  return shopList;
}

export function getOrderItem(
  itemInShopCart: ShopCartItem,
  clientItem: ShopItem,
  productType: string,
  shopCart: ShopCart | Order
) {
  // con base en un articulo  y se busca el orderProduct o orderBenefit o orderSubscription
  let orderItem: OrderProduct | OrderBenefit | OrderSubscription;

  if (productType === 'product') {
    orderItem = shopCart.products.find(
      (product) =>
        product.variant._id === itemInShopCart.variant &&
        product.client === clientItem.client
    );
  } else if (productType === 'benefit') {
    orderItem = shopCart.benefits.find(
      (benefit) =>
        benefit.benefit._id === itemInShopCart.benefit &&
        benefit.client === clientItem?.client &&
        benefit.value === itemInShopCart?.price
    );
  } else if (productType === 'plan') {
    orderItem = shopCart.subscriptions.find(
      (sub) =>
        sub.plan._id === itemInShopCart.plan && sub.client === clientItem.client
    );
  }

  return orderItem;
}
